import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@core-ui/Components/ui/dialog';
import { useTranslation } from 'react-i18next';
import { Button } from '@core-ui/Components/ui/button';
import { AlertTriangle } from 'lucide-react';
import  { type ConfirmDeleteDialogProps, LogLevels } from '@types';
import { writeSuccessLog } from '@lib/logger/logSuccess';
import { writeErrorLog } from '@lib/logger/logError.ts';
import { paintToast } from '@lib/logger/paintToast.ts';

export const ConfirmDeleteDialog: React.FC<ConfirmDeleteDialogProps> = ({
  open,
  onOpenChange,
  intent,
  onDelete,
}) => {
  const [deleting, setDeleting] = useState(false);
  const { t } = useTranslation();

  if (intent == null) {
    return null;
  }

  const handleDelete = async (): Promise<void> => {
    setDeleting(true);

    try {
      const result = await onDelete();

      if (result?.success) {
        await writeSuccessLog(
          t('confirmDeleteDialog.Intent deleted successfully', { lng:'en' }),
          'ConfirmDeleteDialog',
          { intentId: intent.id },
        );
        paintToast(LogLevels.INFO, t('confirmDeleteDialog.Intent deleted successfully'));
      } else {
        await writeErrorLog(
          result?.error?.message ??
            t('confirmDeleteDialog.Failed to delete intent', { lng: 'en' }),
          'ConfirmDeleteDialog',
          { intentId: intent.id },
        );
        paintToast(LogLevels.ERROR, t('confirmDeleteDialog.Failed to delete intent'));
      }
    } catch (error) {
      await writeErrorLog(
        t('confirmDeleteDialog.Failed to delete intent', { lng: 'en' }),
        'ConfirmDeleteDialog',
        { intentId: intent.id, error },
      );
      paintToast(LogLevels.ERROR, t('confirmDeleteDialog.Failed to delete intent'));
    } finally {
      setDeleting(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl shadow-lg border border-border bg-card text-foreground p-6">
        <DialogHeader className="mb-2">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-destructive/30 bg-destructive/10 text-destructive">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <DialogTitle className="text-lg font-semibold">
              {t('confirmDeleteDialog.Delete')} {intent.title}{' '}
              {t('confirmDeleteDialog.Intent')}
            </DialogTitle>
          </div>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {t('confirmDeleteDialog.This will permanently delete the intent')}{' '}
          <strong>{intent.title}</strong>.{' '}
          {t('confirmDeleteDialog.This action cannot be undone.')}
        </p>

        <DialogFooter className="flex justify-end gap-2 mb-0">
          <Button
            data-qa-id="intent-cancel-delete-button"
            variant="outline"
            className="border border-border text-muted-foreground hover:bg-muted"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t('confirmDeleteDialog.Cancel')}
          </Button>
          <Button
            data-qa-id="intent-delete-button"
            variant="destructive"
            className="text-primary-foreground"
            type="submit"
            onClick={() => {
              handleDelete().catch((err) => {
                console.error('Error deleting:', err);
              });
            }}
            disabled={deleting}
          >
            {deleting
              ? t('confirmDeleteDialog.Deleting')
              : t('confirmDeleteDialog.Delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
